import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { formatINR } from "@/lib/products";
import { Check, ChevronRight, Download, Printer, Search, X } from "lucide-react";

const STAGES = ["Order Placed", "Confirmed", "Packed", "Shipped", "Out for Delivery", "Delivered"];

const stageIndex = (status) => {
  const map = { pending: 0, confirmed: 1, packed: 2, shipped: 3, out_for_delivery: 4, delivered: 5 };
  return map[status] ?? 1;
};

const ts = (value) => (value?.seconds ? new Date(value.seconds * 1000).toLocaleString() : "N/A");
const shortId = (id = "") => id.slice(0, 8).toUpperCase();
export default function Orders() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [trackingNotice, setTrackingNotice] = useState("");
  const [params] = useSearchParams();
  const newOrderId = params.get("new");

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const email = (user.email || "").trim().toLowerCase();
        const queries = [
          query(collection(db, "orders"), where("userId", "==", user.uid)),
          ...(email
            ? [
                query(collection(db, "orders"), where("emailLower", "==", email)),
                query(collection(db, "orders"), where("email", "==", user.email)),
              ]
            : []),
        ];
        const snaps = await Promise.all(queries.map((orderQuery) => getDocs(orderQuery)));
        const byId = new Map();
        snaps.forEach((snap) => {
          snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
        });
        const list = Array.from(byId.values());
        list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setOrders(list);
        if (newOrderId) setSelectedBill(list.find((order) => order.id === newOrderId) || null);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [user, newOrderId]);

  const current = useMemo(() => orders.find((order) => order.id === newOrderId) || orders[0], [orders, newOrderId]);
  const idx = current ? stageIndex(current.status || "confirmed") : 1;
  const copyTracking = async (order) => {
    if (!order?.trackingNumber) {
      setTrackingNotice("Tracking ID not added yet.");
      return;
    }
    await navigator.clipboard?.writeText(order.trackingNumber);
    setTrackingNotice("Tracking ID copied. You can see tracking by searching this tracking ID with the service provider name.");
  };

  return (
    <div data-testid="orders-page" className="container-nc py-12">
      <div className="flex items-center gap-2 text-xs text-muted_brown mb-6">
        <Link to="/" className="hover:text-espresso">Home</Link>
        <ChevronRight size={12}/>
        <Link to="/dashboard" className="hover:text-espresso">Dashboard</Link>
        <ChevronRight size={12}/>
        <span className="text-espresso">Orders</span>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl text-espresso">My Orders</h1>
          <p className="text-muted_brown mt-2">Track current orders or view your order history.</p>
        </div>
        <div className="flex items-center gap-2 bg-ivory border border-line rounded-full px-4 py-2 w-full sm:w-80">
          <Search size={14} className="text-muted_brown"/>
          <input placeholder="Search orders..." className="bg-transparent flex-1 outline-none text-sm" data-testid="orders-search"/>
        </div>
      </div>

      {loading && <div className="mt-10 text-muted_brown">Loading...</div>}

      {!loading && !user && (
        <div className="mt-10 bg-ivory border border-line rounded-xl p-8 text-center">
          <p className="text-muted_brown">Please log in to view your orders.</p>
          <Link to="/login" className="btn-primary mt-4">Log In</Link>
        </div>
      )}

      {!loading && user && orders.length === 0 && (
        <div className="mt-10 bg-ivory border border-line rounded-xl p-8 text-center">
          <p className="text-muted_brown">No orders yet.</p>
          <Link to="/shop" className="btn-primary mt-4">Start Shopping</Link>
        </div>
      )}

      {current && (
        <>
          {/* Current order tracking */}
          <div className="mt-10 bg-ivory border border-line rounded-xl overflow-hidden">
            <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-line">
              <div>
                <p className="overline mb-1">Current Order</p>
                <div className="font-serif text-2xl text-espresso">#{shortId(current.id)}</div>
                <div className="text-xs text-muted_brown mt-2">
                  Service provider: <span className="text-espresso">{current.trackingProvider || "Service provider not added yet"}</span>
                  <span className="mx-2">|</span>
                  Tracking ID: <span className="text-espresso">{current.trackingNumber || "Not added yet"}</span>
                </div>
                {current.trackingNumber && (
                  <div className="text-xs text-muted_brown mt-1">
                    You can see tracking by searching this tracking ID with the service provider name.
                  </div>
                )}
                {trackingNotice && <div className="text-xs text-amber-700 mt-2">{trackingNotice}</div>}
                <div className="text-xs text-muted_brown mt-1">Estimated delivery: 5–7 business days</div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => copyTracking(current)} className="btn-outline text-xs py-2 px-4" data-testid="track-btn">
                  {current.trackingNumber ? "Copy Tracking ID" : "Track Package"}
                </button>
                <button onClick={() => setSelectedBill(current)} className="btn-outline text-xs py-2 px-4 flex items-center gap-2" data-testid="invoice-btn">
                  <Download size={12}/> Invoice
                </button>
              </div>
            </div>

            {/* Timeline */}
            <div className="p-6 md:p-10 bg-cream">
              <div className="relative">
                <div className="absolute top-4 left-0 right-0 h-px bg-line">
                  <div className="h-full bg-gold" style={{ width: `${(idx / (STAGES.length - 1)) * 100}%` }}/>
                </div>
                <div className="relative flex justify-between">
                  {STAGES.map((s, i) => {
                    const done = i <= idx;
                    return (
                      <div key={s} className="flex flex-col items-center text-center max-w-[80px]">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                          done ? "bg-gold border-gold text-cream" : "bg-cream border-line text-muted_brown"
                        }`}>
                          {done ? <Check size={13}/> : <div className="w-1.5 h-1.5 rounded-full bg-line"/>}
                        </div>
                        <div className={`mt-3 text-[10px] uppercase tracking-wider ${done ? "text-espresso" : "text-muted_brown"}`}>
                          {s}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="p-6 border-t border-line">
              {(current.items || []).map((i, idx) => (
                <div key={idx} className="flex items-center gap-4 py-3">
                  <img src={i.image} alt="" className="w-14 h-14 rounded-lg object-cover"/>
                  <div className="flex-1">
                    <div className="text-espresso text-sm">{i.name}</div>
                    <div className="text-xs text-muted_brown">Qty {i.qty}</div>
                  </div>
                  <div className="text-espresso text-sm">{formatINR((i.priceINR || 0) * (i.qty || 1))}</div>
                </div>
              ))}
              <div className="border-t border-line pt-3 mt-3 flex justify-between">
                <span className="text-muted_brown text-sm">Total</span>
                <span className="text-espresso font-medium">{formatINR(current.total || 0)}</span>
              </div>
            </div>
          </div>

          {/* History table */}
          <div className="mt-10">
            <h2 className="font-serif text-3xl text-espresso mb-5">Order History</h2>
            <div className="border border-line rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-ivory">
                  <tr className="text-left text-xs uppercase tracking-[0.15em] text-muted_brown">
                    <th className="px-5 py-4">Order ID</th>
                    <th className="px-5 py-4">Items</th>
                    <th className="px-5 py-4">Total</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody className="bg-cream">
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-line" data-testid={`order-row-${o.id}`}>
                      <td className="px-5 py-4 text-espresso">#{shortId(o.id)}</td>
                      <td className="px-5 py-4 text-muted_brown">{(o.items?.length || 0)} item(s)</td>
                      <td className="px-5 py-4 text-espresso">{formatINR(o.total || 0)}</td>
                      <td className="px-5 py-4">
                        <span className="inline-block px-3 py-1 rounded-full bg-champagne text-espresso text-xs">
                          {o.status || "confirmed"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button onClick={() => setSelectedBill(o)} className="text-xs text-espresso underline">View Bill</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {selectedBill && <BillModal order={selectedBill} onClose={() => setSelectedBill(null)} />}
    </div>
  );
}

function BillModal({ order, onClose }) {
  const address = order.shippingAddress || {};
  const customerName = `${address.firstName || ""} ${address.lastName || ""}`.trim() || order.email || "Customer";
  const paymentId = order.razorpayPaymentId || order.paymentId || "N/A";

  const printBill = () => window.print();

  const downloadBill = () => {
    const lines = [
      "Thank you for your order - Note Console",
      `Order ID: ${order.id}`,
      `Payment ID: ${paymentId}`,
      `Service provider: ${order.trackingProvider || "Service provider not added yet"}`,
      `Tracking ID: ${order.trackingNumber || "Not added yet"}`,
      `Date: ${ts(order.createdAt)}`,
      `Name: ${customerName}`,
      `Email: ${order.email || ""}`,
      `Phone: ${address.phone || ""}`,
      `Place: ${[address.city, address.state, address.pincode].filter(Boolean).join(", ")}`,
      "",
      "Purchase Details:",
      ...(order.items || []).map((item) => {
        const options = [item.size, item.cover, item.personalization, item.pageColor, item.color].filter(Boolean).join(" / ");
        return `${item.name}${options ? ` (${options})` : ""} x ${item.qty || 1} - ${formatINR((item.priceINR || 0) * (item.qty || 1))}`;
      }),
      "",
      `Subtotal: ${formatINR(order.subtotal || 0)}`,
      `Shipping: ${formatINR(order.shipping || 0)}`,
      `Tax: ${formatINR(order.tax || 0)}`,
      `Discount: ${formatINR(order.couponDiscount || 0)}`,
      `Total Paid: ${formatINR(order.total || 0)}`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `note-console-bill-${shortId(order.id)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 px-4 py-6 overflow-y-auto print:static print:bg-white print:p-0">
      <div data-print-bill className="max-w-3xl mx-auto bg-cream border border-line rounded-2xl shadow-hover overflow-hidden print:shadow-none print:border-0">
        <div className="bg-espresso text-cream p-8 relative">
          <button onClick={onClose} className="absolute right-4 top-4 w-9 h-9 rounded-full border border-cream/30 flex items-center justify-center print:hidden" aria-label="Close bill">
            <X size={16} />
          </button>
          <p className="text-xs uppercase tracking-[0.28em] text-champagne">Thank you</p>
          <h2 className="font-serif text-4xl mt-3">Your Note Console bill</h2>
          <p className="text-cream/70 mt-3 text-sm">We received your order and are preparing it with care.</p>
        </div>

        <div className="p-8">
          <div className="grid md:grid-cols-2 gap-5">
            <BillBox label="Order ID" value={`#${shortId(order.id)}`} />
            <BillBox label="Payment ID" value={paymentId} />
            <BillBox label="Order Date" value={ts(order.createdAt)} />
            <BillBox label="Payment Status" value={order.paymentStatus || "pending"} />
            <BillBox label="Service Provider" value={order.trackingProvider || "Service provider not added yet"} />
            <BillBox label="Tracking ID" value={order.trackingNumber || "Not added yet"} />
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-8">
            <div>
              <p className="overline mb-3">Customer</p>
              <div className="text-espresso font-medium">{customerName}</div>
              <div className="text-sm text-muted_brown mt-1">{order.email}</div>
              <div className="text-sm text-muted_brown mt-1">{address.phone || "N/A"}</div>
            </div>
            <div>
              <p className="overline mb-3">Place</p>
              <div className="text-sm text-muted_brown leading-relaxed">
                {[address.address, address.landmark, address.city, address.state, address.pincode, address.country].filter(Boolean).join(", ")}
              </div>
            </div>
          </div>

          <div className="mt-8 border border-line rounded-xl overflow-hidden">
            <div className="bg-ivory px-5 py-4 font-serif text-xl text-espresso">Purchase Details</div>
            <div className="divide-y divide-line">
              {(order.items || []).map((item, idx) => (
                <div key={idx} className="px-5 py-4 flex gap-4 items-center">
                  <img src={item.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-ivory" />
                  <div className="flex-1">
                    <div className="text-espresso">{item.name}</div>
                    <div className="text-xs text-muted_brown">Qty {item.qty || 1} {[item.size, item.cover, item.personalization, item.pageColor, item.color].filter(Boolean).join(" / ")}</div>
                  </div>
                  <div className="text-espresso">{formatINR((item.priceINR || 0) * (item.qty || 1))}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 ml-auto max-w-sm space-y-2 text-sm">
            <BillTotal label="Subtotal" value={formatINR(order.subtotal || 0)} />
            <BillTotal label="Shipping" value={formatINR(order.shipping || 0)} />
            <BillTotal label="Tax" value={formatINR(order.tax || 0)} />
            {(order.couponDiscount || 0) > 0 && <BillTotal label="Discount" value={`-${formatINR(order.couponDiscount || 0)}`} />}
            <div className="border-t border-line pt-3 flex justify-between font-serif text-xl text-espresso">
              <span>Total Paid</span>
              <span>{formatINR(order.total || 0)}</span>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap justify-end gap-3 print:hidden">
            <button onClick={downloadBill} className="btn-outline text-xs py-2.5 px-5"><Download size={14} /> Download Bill</button>
            <button onClick={printBill} className="btn-primary text-xs py-2.5 px-5"><Printer size={14} /> Print Bill</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BillBox({ label, value }) {
  return (
    <div className="bg-ivory border border-line rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted_brown">{label}</div>
      <div className="text-espresso mt-2 break-words">{value || "N/A"}</div>
    </div>
  );
}

function BillTotal({ label, value }) {
  return (
    <div className="flex justify-between text-muted_brown">
      <span>{label}</span>
      <span className="text-espresso">{value}</span>
    </div>
  );
}
